import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import cn from 'classnames';
import DatePicker from 'components/DatePicker';
import Dialog from 'components/Dialog';
import { RadioButtonCell } from 'components/RadioButton';
import TimePicker from 'components/TimePicker';
import { Button, TextField } from 'components/UtilElements';
import * as dt from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';

import { ReactComponent as EditIcon } from 'assets/edit.svg';
import { ReactComponent as ErrorIcon } from 'assets/warning.svg';

import { convertHoursToMinutes, convertMinutesToHoursAndMinutes } from './helper';

enum TimeError {
  NONE,
  INCONVENIENT,
  INVALID,
}

interface CreateClassDialogProps {
  show: boolean;
  course: CourseDetails | null;
  onClose(newClass?: CourseTimelineClass): void;
}

const isTimeInconvenient = (time: TimeIn24Hours) => {
  const minTime: TimeIn24Hours = { hours: 8, minutes: 0 };
  const maxTime: TimeIn24Hours = { hours: 20, minutes: 0 };
  return dt.compareTime(minTime, '>', time) || dt.compareTime(time, '>', maxTime);
};

const generateClassId = (date: Date, startTime: TimeIn24Hours, endTime: TimeIn24Hours) => {
  const sTime = dt.serialize24HoursTime(startTime, 24).replace(':', '');
  const eTime = dt.serialize24HoursTime(endTime, 24).replace(':', '');
  return dt.format(date!, 'yyyyMMdd') + sTime + eTime;
};

const MINUTES_IN_ONE_DAY = 1440;

const CreateClassDialog: FC<CreateClassDialogProps> = ({ show, course, onClose }) => {
  const [date, setDate] = useState<Date>();
  const [dateError, setDateError] = useState(false);

  const [from, setFrom] = useState<TimeIn24Hours>({ hours: 10, minutes: 0 });
  const [fromError, setFromError] = useState(TimeError.NONE);

  const [till, setTill] = useState<TimeIn24Hours>({ hours: 10, minutes: 50 });
  const [tillError, setTillError] = useState(TimeError.NONE);

  const [isOnlineMeeting, setIsOnlineMeeting] = useState<NumericBoolean | null>(null);
  const [selectClassModalityError, setSelectClassModalityError] = useState(false);

  const [venue, setVenue] = useState('');
  const [venueError, setVenueError] = useState(false);

  const validateTime = useCallback((from: TimeIn24Hours, till: TimeIn24Hours) => {
    if (dt.compareTime(from, '>=', till)) {
      setTillError(TimeError.INVALID);
    } else if (isTimeInconvenient(till)) {
      setTillError(TimeError.INCONVENIENT);
    } else {
      setTillError(TimeError.NONE);
    }

    if (isTimeInconvenient(from)) {
      setFromError(TimeError.INCONVENIENT);
    } else {
      setFromError(TimeError.NONE);
    }
  }, []);

  const updateFromTime = useCallback(
    (from: TimeIn24Hours) => {
      const add50min = convertHoursToMinutes(from) + 50;

      let updateTime: TimeIn24Hours | null = null;

      if (add50min >= MINUTES_IN_ONE_DAY) {
        updateTime = {
          hours: 23,
          minutes: 59,
        };
      } else {
        updateTime = convertMinutesToHoursAndMinutes(add50min);
      }

      setTill(updateTime);
      setFrom(from);
      validateTime(from, updateTime);
    },
    [validateTime]
  );

  const updateTillTime = useCallback(
    (till: TimeIn24Hours) => {
      setTill(till);
      validateTime(from, till);
    },
    [from, validateTime]
  );

  const data = useMemo(() => {
    if (!course) return null;
    return {
      minDate: dt.fromUnix(Math.max(course.dates.startDate, dt.unix())),
      maxDate: dt.fromUnix(course.dates.endDate),
    };
  }, [course]);

  const onSubmit = useCallback(() => {
    let isValid = fromError !== TimeError.INVALID && tillError !== TimeError.INVALID;

    if (!date) {
      isValid = false;
      setDateError(true);
    }

    if (isOnlineMeeting === null) {
      isValid = false;
      setSelectClassModalityError(true);
    }

    if (isOnlineMeeting === 0 && !venue) {
      isValid = false;
      setVenueError(true);
    }

    if (isValid) {
      const formattedDate = dt.format(date!, 'yyyy-MM-dd');

      const startDateAndTime = new Date(date!);
      startDateAndTime.setHours(from.hours, from.minutes, 0, 0);

      const endDateAndTime = new Date(date!);
      endDateAndTime.setHours(till.hours, till.minutes, 0, 0);

      onClose({
        id: generateClassId(date!, from, till),
        startTime: `${formattedDate}T${dt.serialize24HoursTime(from, 24)}`,
        startTimeUnix: Math.round(startDateAndTime.getTime() / 1000),
        endTime: `${formattedDate}T${dt.serialize24HoursTime(till, 24)}`,
        endTimeUnix: Math.round(endDateAndTime.getTime() / 1000),
        venue,
        isOnlineMeeting: isOnlineMeeting!,
        isNew: true,
      });
    }
  }, [date, from, fromError, isOnlineMeeting, onClose, till, tillError, venue]);

  useEffect(() => {
    if (!show) return;
    setFrom({ hours: 10, minutes: 0 });
    setTill({ hours: 10, minutes: 50 });
    setFromError(TimeError.NONE);
    setTillError(TimeError.NONE);
    setSelectClassModalityError(false);
  }, [show]);

  return (
    <Dialog
      isOpen={show}
      onClose={onClose}
      classes={{ body: 'add-class' }}
      title='Adding classes'
      footer={
        <>
          <Button label='CANCEL' type='secondary' onRipple={() => onClose()} />
          <Button label='ADD' type='success' onRipple={onSubmit} />
        </>
      }
    >
      <div className='add-class__cell'>
        <span>This class will happen on</span>
        <DatePicker
          minDate={data?.minDate}
          maxDate={data?.maxDate}
          onChange={(d) => {
            setDate(d);
            setDateError(false);
          }}
        >
          <span className='add-class__edit-cell'>
            {date ? dt.format(date, DATE_FORMAT) : 'Select Date'}
            <EditIcon />
          </span>
        </DatePicker>
        {dateError && (
          <div className='add-class__error form-field--error'>
            <ErrorIcon />
            <span>Please select date</span>
          </div>
        )}
      </div>
      <div className='add-class__cell'>
        <span>From (Start Time)</span>
        <TimePicker value={from} onChange={updateFromTime} />
        {fromError === TimeError.INCONVENIENT ? (
          <div className='add-class__error form-field--warning'>
            <ErrorIcon />
            <span>This time seems a little inconvenient</span>
          </div>
        ) : null}
      </div>
      <div className='add-class__cell'>
        <span>Till (End Time)</span>
        <TimePicker value={till} onChange={updateTillTime} />
        {tillError === TimeError.INCONVENIENT ? (
          <div className='add-class__error form-field--warning'>
            <ErrorIcon />
            <span>This time seems a little inconvenient</span>
          </div>
        ) : tillError === TimeError.INVALID ? (
          <div className='add-class__error form-field--error'>
            <ErrorIcon />
            <span>Class end time should be after the class start time</span>
          </div>
        ) : null}
      </div>
      <div
        className={cn('add-class__section-heading', {
          'has-error': selectClassModalityError,
        })}
      >
        Select class modality {selectClassModalityError && '- Please select one option'}
      </div>
      <RadioButtonCell
        className='add-class__radio-cell'
        isSelected={isOnlineMeeting === 1}
        onToggle={() => setIsOnlineMeeting((isOnlineMeeting) => (isOnlineMeeting === 1 ? null : 1))}
      >
        Online on Acadly (with Zoom plugin)
      </RadioButtonCell>
      <RadioButtonCell
        className='add-class__radio-cell'
        isSelected={isOnlineMeeting === 0}
        onToggle={() => setIsOnlineMeeting((isOnlineMeeting) => (isOnlineMeeting === 0 ? null : 0))}
      >
        <span>In-person (or on another online platform)</span>
      </RadioButtonCell>
      <CSSTransition in={isOnlineMeeting === 0} classNames='fade' timeout={300} unmountOnExit>
        <div className='add-class__cell'>
          <span>At (venue/link)</span>
          <TextField
            selector='add-class__input'
            wrapperClassName='add-class__input-wrapper'
            placeholder='Type venue/link here'
            value={venue}
            onchange={(e) => setVenue(e.target.value)}
          />
          {venueError && (
            <div className='add-class__error form-field--error'>
              <ErrorIcon />
              <span>Please enter venue</span>
            </div>
          )}
        </div>
      </CSSTransition>
    </Dialog>
  );
};

export default CreateClassDialog;
